/**
 * OralCancerPreventionCard — Phase D Section 9.
 *
 * Educational module focusing ONLY on modifiable risk factors and
 * health-optimisation score. STRICTLY NO disease prediction, NO diagnostic
 * output, NO cancer prediction.
 *
 * "Modifiable risk factors" displayed:
 *   - Tobacco use
 *   - Alcohol intake
 *   - HPV awareness (information only)
 *   - Poor diet quality
 *   - Chronic inflammation
 *
 * Outputs an "Oral Health Optimization Score" (0-100) which is the inverse
 * of accumulated modifiable risks. The score is purely educational.
 */
import React from "react";
import { ShieldAlert, ShieldCheck, Info, ExternalLink } from "lucide-react";
import ScoreExplanation from "./ScoreExplanation";

const BAND = {
  green:  "#22C55E",
  yellow: "#EAB308",
  orange: "#F97316",
  red:    "#EF4444",
};

export function computeOptimizationScore(signals) {
  let s = 100;
  const factors = [];

  if (signals.smoking) {
    s -= 30;
    factors.push({ key: "tobacco", weight: -30, label_bg: "Тютюн", label_en: "Tobacco" });
  } else {
    factors.push({ key: "no_tobacco", weight: +0, label_bg: "Без тютюн ✓", label_en: "No tobacco ✓", protective: true });
  }

  const alc = signals.alcohol_per_week || 0;
  if (alc > 14) {
    s -= 20;
    factors.push({ key: "alcohol_high", weight: -20, label_bg: `Алкохол ${alc.toFixed(0)}/седм. (висок)`, label_en: `Alcohol ${alc.toFixed(0)}/wk (high)` });
  } else if (alc > 7) {
    s -= 10;
    factors.push({ key: "alcohol_mod", weight: -10, label_bg: `Алкохол ${alc.toFixed(0)}/седм. (умерен)`, label_en: `Alcohol ${alc.toFixed(0)}/wk (moderate)` });
  } else {
    factors.push({ key: "alcohol_low", weight: +0, label_bg: "Нисък алкохол ✓", label_en: "Low alcohol ✓", protective: true });
  }

  const fiber = signals.fiber_per_day || 0;
  if (fiber >= 25) {
    factors.push({ key: "diet_high_fiber", weight: +5, label_bg: "Високи фибри ✓", label_en: "High fiber ✓", protective: true });
  } else if (fiber < 15) {
    s -= 10;
    factors.push({ key: "diet_low_fiber", weight: -10, label_bg: "Ниски фибри", label_en: "Low fiber" });
  }

  const sugar = signals.sugar_per_day || 0;
  if (sugar > 50) {
    s -= 10;
    factors.push({ key: "high_sugar", weight: -10, label_bg: "Високи захари", label_en: "High sugar" });
  }

  const crp = signals.hs_crp || 0;
  if (crp > 3) {
    s -= 10;
    factors.push({ key: "chronic_inflammation", weight: -10, label_bg: "Хронично възпаление (CRP)", label_en: "Chronic inflammation (CRP)" });
  }

  s = Math.max(0, Math.min(100, s));
  return { score: s, factors };
}

export default function OralCancerPreventionCard({ signals, isDark, lang = "bg", cardCls }) {
  const bg = lang === "bg";
  const { score, factors } = computeOptimizationScore(signals || {});
  const band = score >= 75 ? "green" : score >= 55 ? "yellow" : score >= 35 ? "orange" : "red";
  const color = BAND[band];

  const t = bg
    ? {
        title: "Превенция — модифицируеми фактори",
        score: "Оценка за оптимизация на здравето",
        protective: "Защитни",
        burden: "Натоварващи",
        disclaimer: "Образователно. НЕ прогнозираме онкологично заболяване. Този модул показва само модифицируеми навици.",
        hpvInfo: "Информация за HPV ваксина: HPV ваксинацията се препоръчва от СЗО. Консултирайте се с лекар.",
        hpvLink: "Към СЗО — HPV и онкологични заболявания",
      }
    : {
        title: "Prevention — modifiable factors",
        score: "Health Optimization Score",
        protective: "Protective",
        burden: "Burden",
        disclaimer: "Educational. We do NOT predict oncological disease. This module shows modifiable habits only.",
        hpvInfo: "HPV vaccine info: vaccination is recommended by WHO. Consult your doctor.",
        hpvLink: "WHO — HPV & cancer",
      };

  const protective = factors.filter((f) => f.protective);
  const burden = factors.filter((f) => !f.protective);

  return (
    <div className={`${cardCls} p-5`} data-testid="dhi-oral-cancer-prev-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-lg font-bold flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-emerald-500" />
          {t.title}
        </h3>
        <div className="flex items-center gap-2">
          <div className="relative" style={{ width: 60, height: 60 }}>
            <svg width="60" height="60" viewBox="0 0 60 60">
              <circle cx="30" cy="30" r="24" stroke="rgba(0,0,0,0.06)" strokeWidth="6" fill="none" />
              <circle
                cx="30" cy="30" r="24"
                stroke={color} strokeWidth="6" strokeLinecap="round" fill="none"
                strokeDasharray={`${(score / 100) * 150.8} 150.8`}
                transform="rotate(-90 30 30)"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center text-base font-bold">
              {score}
            </div>
          </div>
        </div>
      </div>

      <p className={`text-[10px] mb-3 px-2 py-1.5 rounded ${isDark ? "bg-amber-500/10 text-amber-300" : "bg-amber-50 text-amber-700"}`}>
        <Info className="w-3 h-3 inline mr-1" /> {t.disclaimer}
      </p>

      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <div>
          <div className="text-xs font-bold mb-1.5 text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5" /> {t.protective}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {protective.length === 0 && <span className="text-[10px] opacity-40 italic">—</span>}
            {protective.map((f, i) => (
              <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full ${isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-50 text-emerald-700"}`}>
                {bg ? f.label_bg : f.label_en}
              </span>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs font-bold mb-1.5 text-rose-600 dark:text-rose-400 flex items-center gap-1">
            <ShieldAlert className="w-3.5 h-3.5" /> {t.burden}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {burden.length === 0 && <span className="text-[10px] opacity-40 italic">—</span>}
            {burden.map((f, i) => (
              <span key={i} className={`text-[10px] px-2 py-0.5 rounded-full ${isDark ? "bg-rose-500/15 text-rose-300" : "bg-rose-50 text-rose-700"}`}>
                {bg ? f.label_bg : f.label_en} {f.weight}
              </span>
            ))}
          </div>
        </div>
      </div>

      <a
        href="https://www.who.int/news-room/fact-sheets/detail/human-papilloma-virus-and-cancer"
        target="_blank" rel="noopener noreferrer"
        className={`flex items-center gap-2 text-xs p-2 rounded ${isDark ? "bg-white/[0.02] hover:bg-white/5" : "bg-gray-50 hover:bg-gray-100"}`}
        data-testid="dhi-hpv-info-link"
      >
        <Info className="w-3.5 h-3.5 text-sky-500" />
        <span className="flex-1">
          <strong>{t.hpvInfo}</strong>
          <span className="opacity-60 ml-2">— {t.hpvLink} <ExternalLink className="w-2.5 h-2.5 inline" /></span>
        </span>
      </a>

      <ScoreExplanation
        isDark={isDark}
        lang={lang}
        testid="dhi-cancer-prev-why"
        inputs={lang === "en"
          ? "Smoking, alcohol per week, daily fiber, daily sugar, hs-CRP. These are the modifiable factors associated with oral cancer risk by WHO and major oncology societies."
          : "Тютюнопушене, алкохол на седмица, дневни фибри, дневна захар, hs-CRP. Това са модифицируемите фактори, асоциирани с риск за орален рак според СЗО и водещите онкологични общества."}
        formula={lang === "en"
          ? "Starts at 100. Subtracts: smoking −30, alcohol > 14 units/wk −20 (> 7 units/wk −10), low fiber (< 15g) −10, high sugar (> 50g) −10, chronic inflammation (CRP > 3) −10."
          : "Започва от 100. Изважда: пушене −30, алкохол > 14 единици/седм. −20 (> 7 единици/седм. −10), ниски фибри (< 15g) −10, висока захар (> 50g) −10, хронично възпаление (CRP > 3) −10."}
        factors={factors.map((f) => `${bg ? f.label_bg : f.label_en} ${f.weight ? (f.weight > 0 ? `+${f.weight}` : f.weight) : ""}`)}
      />
    </div>
  );
}
